import React, { useEffect, useState, createContext, useContext } from 'react';
import { Product, CartItem } from '../types/models';
interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  updateCartItem: (productId: string, updates: Partial<CartItem>) => void;
  clearCart: () => void;
  cartCount: number;
  cartTotal: number;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cart, setCart] = useState<CartItem[]>([]);
  // Load cart from localStorage on mount
  useEffect(() => {
    const storedCart = localStorage.getItem('bloom-pop-cart');
    if (storedCart) {
      setCart(JSON.parse(storedCart));
    }
  }, []);
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bloom-pop-cart', JSON.stringify(cart));
  }, [cart]);
  const addToCart = (product: Product, quantity: number = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        return prevCart.map(item => item.product.id === product.id ? {
          ...item,
          quantity: item.quantity + quantity
        } : item);
      }
      return [...prevCart, {
        product,
        quantity,
        // Default values
        deliveryType: 'delivery',
        isGift: false
      }];
    });
  };
  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };
  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart => prevCart.map(item => item.product.id === productId ? {
      ...item,
      quantity
    } : item));
  };
  const updateCartItem = (productId: string, updates: Partial<CartItem>) => {
    setCart(prevCart => prevCart.map(item => item.product.id === productId ? {
      ...item,
      ...updates
    } : item));
  };
  const clearCart = () => {
    setCart([]);
  };
  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);
  const cartTotal = cart.reduce((total, item) => total + item.product.price * item.quantity, 0);
  return <CartContext.Provider value={{
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateCartItem,
    clearCart,
    cartCount,
    cartTotal
  }} data-id="element-1189">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}